import React from 'react';
import { Layers, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'components/ui/skeleton';

export interface RelativeSearchQuestion {
    title: string;
    imgSrc: string | null;
    url: string | null;
    iframeSrc: string | null;
}

interface SearchRelatedListProps {
    questionList: any;
    onSendMessage: (message: string, files: any[]) => void;
    messageId?: string;
    isSkeletonShowForQuestions?: boolean;
}

export default function SearchRelatedList({ questionList, onSendMessage, messageId, isSkeletonShowForQuestions }: SearchRelatedListProps) {
    const questions = questionList?.data || [];
    const { t } = useTranslation();

    const toggleAccordion = (message: string) => {
        if (!onSendMessage) return;
        // Preserve internal newlines while removing only leading/trailing whitespace
        let cleanedMessage = message.replace(/^\s+|\s+$/g, '');
        // Convert single newlines to markdown line breaks (two spaces + newline)
        cleanedMessage = cleanedMessage.replace(/\n/g, '  \n');
        onSendMessage(cleanedMessage, []);
    };

    if (isSkeletonShowForQuestions) {
        return (
            <div className="flex justify-center p-4 flex-col">
                <div className="flex items-center gap-2 mb-4">
                    <Skeleton className="h-5 w-5 rounded" />
                    <Skeleton className="h-6 w-24" />
                </div>

                <div className="w-full space-y-2">
                    {[...Array(4)].map((_, i) => (
                        <div key={`${messageId}-skeleton-${i}`}>
                            <div className="w-full flex items-start justify-between py-2">
                                <Skeleton className="h-5 w-full max-w-md" />
                                <Skeleton className="h-6 w-6 rounded flex-shrink-0 ml-4" />
                            </div>
                            {i < 3 && <div className="border-b bg-zinc-300" />}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (questions.length) {
        return (
            <div className="flex justify-center p-4 flex-col">
                {questions.length ?
                    <div className="flex items-center gap-2 mb-4">
                        <Layers size={20} />  <span className="text-lg fw-500">{t('RELATED')}</span>
                    </div> : null}

                <div className="w-full">
                    {questions?.map((question: RelativeSearchQuestion, index: number) => (
                        <div key={`${messageId}-question-${index}-${question.title}`}>
                            <button
                                onClick={() => toggleAccordion(question.title)}
                                className="w-full flex items-start justify-between py-2 text-left transition-colors group"
                            >
                                <span className="font-normal text-base hover:text-primary leading-relaxed pr-4">
                                    {question.title}
                                </span>
                                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-primary mt-0.5">
                                    <Plus size={20} className="transition-transform" />
                                </div>
                            </button>

                            {index < questions.length - 1 && (
                                <div className="border-b bg-zinc-300" />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    } else {
        return null;
    }
}